<template>
  <div
    v-if="showErrors && filteredErrors.length"
    class="q-pa-md"
  >
    <div :class="`${errorType}s`">
      <re-banner
        v-for="error in filteredErrors"
        :key="error.id"
        :color="bannerColor"
        :class="[`re-${errorType}`, `re-${errorType}-${error.message}`, 'q-mt-sm']"
        :title="getErrorTitle(error)"
        inline-actions
      >
        <div class="flex">
          <p v-if="getErrorMessage(error)">
            {{ getErrorMessage(error) }}
          </p>
          <p v-else>
            {{ getFallbackMessage(error) }}
          </p>
          <re-icon
            name="mdi-content-copy"
            size="xs"
            :color="bannerColor"
            class="q-ml-sm cursor-pointer"
            @click="expandError(error)"
          />
        </div>

        <div
          v-if="hasDebugInfo(error) && expandedErrorId === error.id"
          class="q-mt-sm text-caption text-black"
        >
          <div class="q-pa-sm">
            <p
              v-if="getErrorCode(error)"
              class="q-mb-xs"
            >
              <strong>{{ t('global.error.code') }}:</strong>
              {{ getErrorCode(error) }}
            </p>
            <p
              v-if="error.debug?.message"
              class="q-mb-xs"
            >
              <strong>{{ t('global.error.message') }}:</strong>
              {{ error.debug.message }}
            </p>
            <div
              v-if="error.debug?.context"
              class="q-mt-sm"
            >
              <p class="text-weight-medium q-mb-xs">{{ t('global.error.context') }}:</p>
              <ul class="q-pl-md">
                <li
                  v-for="(value, key) in error.debug.context"
                  :key="key"
                >
                  <strong>{{ key }}:</strong>
                  {{ value }}
                </li>
              </ul>
            </div>
            <div
              v-if="error.debug?.traceback"
              class="q-mt-sm"
            >
              <p class="text-weight-medium q-mb-xs">{{ t('global.error.traceback') }}:</p>
              <pre>{{ error.debug.traceback }}</pre>
            </div>
          </div>
        </div>

        <template #action>
          <re-button
            icon="mdi-close"
            dense
            rounded
            size="sm"
            color="white"
            @click="dismissError(error)"
          />
        </template>
      </re-banner>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import enums from '@/plugins/enums.js'
import { Actions } from '@/store/Log'
import copyToClipboard from '@/utils/copyToClipboard'

interface ErrorDebugInfo {
  message?: string
  context?: Record<string, unknown>
  traceback?: string
}

interface ErrorItem {
  id: string
  type: string
  message?: string
  title?: string
  error_code?: string | number
  details?: Record<string, unknown>
  debug?: ErrorDebugInfo
  hidden?: boolean
}

const { errorType = 'error' } = defineProps<{ errorType?: string }>()
const store = useStore()
const { t, te } = useI18n()
const curUser = useCurUser()
const config = useConfig()

const isDev = import.meta.env.MODE === 'development'
const expandedErrorId = ref<string | null>(null)
const errors = computed(() => store.getters.errors as ErrorItem[])

const showErrors = computed(() => {
  // Don't show warnings in production
  if (!isDev && errorType === 'warning') {
    return false
  }
  // Hide errors for non-rockestate users if configured
  return !config.HIDE_FRONTEND_ERRORS || curUser?.organization.value === 'rockestate'
})

const filteredErrors = computed(() => {
  return (errors.value || []).filter((e) => !e.hidden && e.type === errorType)
})

const bannerColor = computed(() => {
  return errorType === enums.feedbackType.WARNING ? enums.feedbackType.WARNING : enums.feedbackType.NEGATIVE
})

function expandError(error: ErrorItem) {
  const isCurrentlyExpanded = expandedErrorId.value === error.id
  expandedErrorId.value = isCurrentlyExpanded ? null : error.id
  if (!isCurrentlyExpanded)
    copyToClipboard(
      `- title: ${getErrorTitle(error)} 
       - message: ${getErrorMessage(error) || getFallbackMessage(error)}
       - type: ${errorType} 
       - code: ${getErrorCode(error)} 
       - trace: ${JSON.stringify(error)}`
    )
}

const dismissError = (error: ErrorItem) => {
  store.dispatch(Actions.HIDE_ERROR, error.id)
}

const getErrorCode = (error: ErrorItem): string | number | undefined => {
  return error.error_code !== undefined ? error.error_code : error.message
}

const getErrorI18nKey = (error: ErrorItem): string => {
  return `${errorType}.${getErrorCode(error)}`
}

const getErrorTitle = (error: ErrorItem): string => {
  // Priority: localized error title > localized error code title > raw title > localized error type
  if (error.title && te(error.title)) {
    return t(error.title)
  }

  const titleKey = `${getErrorI18nKey(error)}.title`
  if (te(titleKey)) {
    return t(titleKey)
  }

  if (error.title) {
    return error.title
  }

  // Friendly fallback based on error type
  const friendlyTitles: Record<string, string> = {
    error: t('global.error.something_went_wrong'),
    warning: t('global.error.heads_up'),
  }

  return friendlyTitles[errorType] || errorType
}

const getErrorMessage = (error: ErrorItem): string | false => {
  const msgKey = `${getErrorI18nKey(error)}.message`
  return te(msgKey) ? t(msgKey, error.details ?? {}) : false
}

const getFallbackMessage = (error: ErrorItem): string => {
  // Show a user-friendly message instead of technical details
  if (error.debug?.message) {
    return error.debug.message
  }

  return t('global.error.error_occurred_please_try_again')
}

const hasDebugInfo = (error: ErrorItem): boolean => {
  return !!(getErrorCode(error) || error.debug?.message || error.debug?.context || error.debug?.traceback)
}
</script>

<style lang="scss" scoped>
pre {
  white-space: pre-wrap;
}
</style>
